/**
 * Delete Address Confirmation Dialog
 * Dumb component - receives all data via props
 */

import { useTranslation } from '@ufc/core'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  cn,
  Trash2,
  AlertTriangle,
} from '@ufc/uikit'
import type { Address } from '../../domain/types'

export interface DeleteAddressDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  address: Address | null
  onConfirm: (address: Address) => void
  isLoading?: boolean
  isRTL?: boolean
}

export function DeleteAddressDialog({
  open,
  onOpenChange,
  address,
  onConfirm,
  isLoading = false,
  isRTL = false,
}: DeleteAddressDialogProps) {
  const { t } = useTranslation()

  if (!address) return null

  const handleConfirm = () => {
    onConfirm(address)
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className={cn(isRTL && 'text-right')}>
        <AlertDialogHeader>
          <div className={cn('flex items-center gap-3 mb-2')}>
            <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-destructive" />
            </div>
            <AlertDialogTitle className="text-lg">
              {t('addressbook.deleteAddress')}
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-base">
            {t('addressbook.deleteConfirmation', { name: address.name })}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* Address Summary */}
        <div className="p-4 rounded-xl bg-slate-50 border border-slate-100 my-4">
          <div className="space-y-2">
            <p className="font-semibold text-slate-800">{address.name}</p>
            <p className="text-sm text-slate-600">{address.address}</p>
            <p className="text-sm text-slate-500">{address.city}, {address.region}</p>
          </div>
        </div>

        <AlertDialogFooter className={cn()}>
          <AlertDialogCancel disabled={isLoading}>
            {t('common.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isLoading}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            <Trash2 className={cn('w-4 h-4', isRTL ? 'ms-2' : 'me-2')} />
            {isLoading ? t('common.deleting') : t('common.delete')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
